import { useQueries, useQuery } from "@tanstack/react-query"
import { totalContentV3QueryRequests } from "@sop/FuelPage/Total/totalContentV3QueryRequests"
import { requestApi2 } from "@utils/http"
import { OpModeGetListArgs, OpModeList, endpoints } from "api"

export function useGetOpModesListQuery({
	plantIdsList,
	shouldFetch = true,
}: {
	plantIdsList?: number[]
	shouldFetch?: boolean
}) {
	const enabled =
		totalContentV3QueryRequests.getOpModesList.enable &&
		shouldFetch &&
		plantIdsList !== undefined &&
		plantIdsList.length > 0

	const queryKey = [
		`queryId=GetOpModesList`,
		`endpoints=[${endpoints.opModeGetList.getName()}]`,
		`plantIdsList=[${plantIdsList}]`,
	]

	const res = useQuery({
		queryKey,
		queryFn: async (): Promise<OpModeList> => {
			if (plantIdsList === undefined) {
				throw new Error(
					"useGetOpModesListQuery requires a non-empty plantIdsList parameter"
				)
			}

			const data: OpModeList = []
			const seenIds = new Set<number>()

			for (const plantId of plantIdsList) {
				if (plantId === -1 || plantId === undefined) {
					continue
				}

				try {
					const args: OpModeGetListArgs = { plantId: plantId }
					const opModes = await requestApi2(endpoints.opModeGetList, args)
					if (opModes !== null && Array.isArray(opModes)) {
						for (const opMode of opModes) {
							if (!seenIds.has(opMode.id)) {
								seenIds.add(opMode.id)
								data.push(opMode)
							}
						}
					} else {
						console.error(`Invalid response for plantId ${plantId}:`, opModes)
					}
				} catch (error) {
					console.error(`Error fetching op modes for plantId ${plantId}:`, error)
					throw error
				}
			}

			return data
		},
		enabled,
		staleTime: 600000,
		refetchInterval: 600000,
	})
	return res
}

export function useGetOpModesListQueryBackup({
	plantIdsList,
	shouldFetch = true,
}: {
	plantIdsList?: number[]
	shouldFetch?: boolean
}) {
	return useQueries({
		queries: (plantIdsList ?? []).map((plantId) => ({
			queryKey: [
				`queryId=GetOpModesList`,
				`endpoints=[${endpoints.opModeGetList.getName()}]`,
				`plantId=[${plantId}]`,
			],
			queryFn: async (): Promise<{
				plantId: number
				opModes:
					| {
							id: number
							name: string
					  }[]
					| null
			} | null> => {
				if (plantId === -1 || plantId === undefined) {
					throw new Error(
						"useGetOpModesListQueryBackup requires each plantId to be defined"
					)
				}

				try {
					const args: OpModeGetListArgs = { plantId: plantId }
					const opModes = await requestApi2(endpoints.opModeGetList, args)
					return { plantId, opModes }
				} catch (error) {
					console.error(`Error fetching op modes for plantId ${plantId}:`, error)
					throw error
				}
			},
			enabled:
				totalContentV3QueryRequests.getOpModesList.enable &&
				shouldFetch &&
				plantId !== -1 &&
				plantId !== undefined,
			staleTime: 600000,
			refetchInterval: 600000,
		})),
		combine:
			// useCallback(
			(results) => {
				const opModeList: OpModeList = []
				const seenIds = new Set<number>()
				for (const q of results) {
					const data = q.data
					if (q.data == undefined) {
						continue
					}
					if (data != undefined && data.opModes !== null && Array.isArray(data.opModes)) {
						for (const opMode of data.opModes) {
							if (!seenIds.has(opMode.id)) {
								seenIds.add(opMode.id)
								opModeList.push(opMode)
							}
						}
					} else {
						console.error(
							`Invalid response for plantId ${q.data.plantId}:`,
							q.data.opModes
						)
					}
				}
				return {
					data: opModeList,
					isFetching: results.some((q) => q.isFetching),
					isLoading: results.some((q) => q.isLoading),
					isError: results.some((q) => q.isError),
					errors: results.map((q) => q.error).filter(Boolean),
				}
			},
		// , []),
	})
}
